import React from 'react';
import './SermonFeedbackAnalytics.css';

function CountBars({ entries, maxCount }) {
  if (!entries || entries.length === 0) {
    return <p style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</p>;
  }
  const cap = maxCount || Math.max(...entries.map((e) => e.count), 1);
  return (
    <div>
      {entries.map((entry) => (
        <div key={entry.name} className="sermon-bar-row">
          <div title={entry.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.name}
          </div>
          <div className="sermon-bar-track">
            <div
              className="sermon-bar-fill"
              style={{ width: `${(entry.count / cap) * 100}%` }}
            />
          </div>
          <div className="sermon-bar-value">{entry.count}</div>
        </div>
      ))}
    </div>
  );
}

function RatingBars({ ratingBreakdown }) {
  const ratings = [1, 2, 3, 4, 5];
  const counts = ratings.map((r) => ratingBreakdown?.[String(r)] || 0);
  const max = Math.max(...counts, 1);
  return (
    <div>
      {ratings.map((r, idx) => (
        <div key={r} className="sermon-bar-row">
          <div>{r} / 5</div>
          <div className="sermon-bar-track">
            <div
              className="sermon-bar-fill sermon-bar-rating-fill"
              style={{ width: `${(counts[idx] / max) * 100}%` }}
            />
          </div>
          <div className="sermon-bar-value">{counts[idx]}</div>
        </div>
      ))}
    </div>
  );
}

function breakdownEntries(obj) {
  if (!obj) return [];
  return Object.entries(obj)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export default function SermonFeedbackAnalytics({ analytics }) {
  if (!analytics) {
    return null;
  }

  const {
    total = 0,
    attended_count: attendedCount = 0,
    not_attended_count: notAttendedCount = 0,
    attendance_rate: attendanceRate = 0,
    avg_sermon_rating: avgRating = 0,
    rating_breakdown: ratingBreakdown = {},
    top_absence_reasons: topAbsenceReasons = [],
    return_likelihood_breakdown: returnLikelihoodBreakdown = {},
    outcome_breakdown: outcomeBreakdown = {},
    total_pastoral_questions: totalPastoralQuestions = 0,
  } = analytics;

  const returnEntries = breakdownEntries(returnLikelihoodBreakdown);
  const outcomeEntries = breakdownEntries(outcomeBreakdown);

  return (
    <div className="card sermon-feedback-analytics">
      <h3>Sermon Feedback Analytics</h3>

      <div className="stats-grid">
        <div className="stat">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Total Responses</div>
        </div>
        <div className="stat">
          <div className="stat-value">{attendanceRate.toFixed(1)}%</div>
          <div className="stat-label">Attendance Rate</div>
        </div>
        <div className="stat">
          <div className="stat-value">{avgRating ? avgRating.toFixed(2) : '—'}</div>
          <div className="stat-label">Avg Sermon Rating</div>
        </div>
        <div className="stat">
          <div className="stat-value">{totalPastoralQuestions}</div>
          <div className="stat-label">Pastoral Questions</div>
        </div>
      </div>

      <div className="sermon-section">
        <h4>Attendance Split</h4>
        <div className="sermon-breakdown-badges">
          <span className="badge badge-positive">Attended: {attendedCount}</span>
          <span className="badge badge-negative">Did not attend: {notAttendedCount}</span>
        </div>
      </div>

      <div className="sermon-section">
        <h4>Sermon Rating Distribution</h4>
        <RatingBars ratingBreakdown={ratingBreakdown} />
      </div>

      <div className="sermon-section">
        <h4>Top Absence Reasons</h4>
        <CountBars entries={topAbsenceReasons} />
      </div>

      <div className="sermon-section">
        <h4>Return Likelihood</h4>
        <CountBars entries={returnEntries} />
      </div>

      <div className="sermon-section">
        <h4>Call Outcomes</h4>
        <div className="sermon-breakdown-badges">
          {outcomeEntries.length === 0 ? (
            <span style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</span>
          ) : (
            outcomeEntries.map((entry) => (
              <span key={entry.name} className="badge badge-neutral">
                {entry.name.replace('_', ' ')}: {entry.count}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
